import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { IDockviewPanelProps } from 'dockview-react';
import { formatServerTime, prefsStore, shellOf, symbolsOf, usePrefsValue, useServerTime } from '@adiox/ui';
import { useSymbols } from '../market/useSymbols';
import { useStream } from '../stream/stream';
import { usePrefs } from '@adiox/ui';
import { useWorkspace } from '../workspace/workspaceStore';
import {
  CHART_TYPES,
  STANDARD_TIMEFRAMES,
  TIMEFRAMES,
  isChartType,
  isTimeframe,
  legendOf,
  timeframeSeconds,
  type Bar,
  type ChartType,
  type Timeframe,
} from './chartModel';
import { readChartColors } from './chartColors';
import { ChartView, type ChartControls } from './ChartView';
import { Icon, type IconName } from './icons';
import { pushHistory, screenshotName, timeframeForKey, toolbarFit } from './toolbar';
import { useTradeMarkers } from './useTradeMarkers';
import { SymbolPicker } from '../trading/SymbolPicker';
import { ContextMenu, DragReadout, PendingConfirm, type MenuItem } from './ChartOverlays';
import { pendingChoicesAt, stopTargetAt, snapPrice, type ChartLine } from './chartTrading';
import type { ContextInfo } from './lineLayer';
import { useChartTrading } from './useChartTrading';
import { useLayout } from '../shell/layout';
import { ConfirmDialog, Modal } from '@adiox/ui';
import { EXTENDABLE, normalizeDrawings, TOOLS, TOOL_LABELS, type Drawing } from '../drawings/model';
import type { ActiveTool } from '../drawings/DrawingLayer';
import {
  applyTemplate,
  cleanName,
  makeTemplate,
  nameProblem,
  normalizeTemplates,
  sizeOf,
  PREFS_LIMIT_BYTES,
} from './templates';
import { IndicatorDialog } from '../indicators/IndicatorDialog';
import { INDICATORS, indicatorById } from '../indicators/library';
import {
  newInstance,
  normalizeInstances,
  renderIndicators,
  type IndicatorInstance,
} from '../indicators/instances';
import { KIND_LABELS, normalizeVolume, sideAllowed, type OrderType } from '../trading/tradeRules';
import { useBars } from './useBars';
import { readout, useDataWindow } from './dataWindowModel';
import { chartColorsWith, normalizeProperties, parseQuickNavigation, periodStarts } from './chartProperties';
import { ChartPropertiesDialog } from './ChartPropertiesDialog';
import { ObjectsList } from '../drawings/ObjectsList';
import { useChartCommands, type ChartCommands } from './chartCommands';

/** What one chart remembers; it is stored with the dock layout, so it comes back with the user's workspace. */
export interface ChartParams {
  symbol?: string;
  tf?: Timeframe;
  type?: ChartType;
  /** Follow the symbol picked in Market Watch. */
  linked?: boolean;
  bidAsk?: boolean;
  /** One-click trading is on for this chart (off by default, and it says so on screen). */
  oneClick?: boolean;
  oneClickVolume?: string;
  /** Indicators on this chart, with their parameters and colours. */
  indicators?: IndicatorInstance[];
  /** Drawings snap to bar prices (the magnet). */
  magnet?: boolean;
  /** Keep the newest bar in view as bars arrive (on unless turned off). */
  autoScroll?: boolean;
  /** Show the account's past trades as arrows (on unless turned off). */
  showTrades?: boolean;
  /** Grid, volumes, separators, shift, scale, trade levels and colours of this chart (F8); see chartProperties.ts. */
  properties?: unknown;
}

/** One button of the chart toolbar, shown as an icon or, when there is no room, as an entry of the More menu. */
interface ToolbarAction {
  id: string;
  icon: IconName;
  /** The accessible name, and the menu text. */
  label: string;
  /** A keyboard shortcut to mention in the tooltip and menu. */
  shortcut?: string;
  /** For on/off tools. */
  pressed?: boolean;
  disabled?: boolean;
  /** Opens a menu (drawing tools, indicators, templates). */
  menu?: boolean;
  onSelect: (anchor: DOMRect) => void;
}

/** A per-symbol undo history of the drawings, kept for the life of the chart. */
type History = Record<string, { undo: Drawing[][]; redo: Drawing[][] }>;

const TYPE_LABELS: Record<ChartType, string> = {
  candles: 'Candles',
  bars: 'Bars',
  line: 'Line',
  area: 'Area',
};
const DEFAULT_SYMBOL = 'EURUSD';
const NO_LINES: never[] = [];

const fmt = (value: number, digits: number) => value.toFixed(digits);

/** One chart window: symbol, timeframe and type picker, history with paging, live bar, bid/ask lines and an OHLC read-out. */
export function ChartPanel({ params, api, containerApi }: IDockviewPanelProps<ChartParams>) {
  const { theme } = usePrefs();
  const { mobile } = useLayout();
  // The classic workspace drives the chart from its main toolbar and menus, so the chart has no toolbar of its own.
  const classic = usePrefsValue((s) => shellOf(s.data) === 'classic') && !mobile;
  const symbols = useSymbols();
  const watched = usePrefsValue((s) => s.data.marketWatch);
  const active = useWorkspace((s) => s.activeSymbol);
  const [hovered, setHovered] = useState<{ key: string; bar: Bar } | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);

  const tf = isTimeframe(params.tf) ? params.tf : 'M15';
  const type = isChartType(params.type) ? params.type : 'candles';
  const linked = params.linked ?? true;
  const bidAsk = params.bidAsk ?? true;
  const list = symbols.data;
  const fallback = useMemo(
    () => symbolsOf({ marketWatch: watched })?.[0] ?? list?.[0]?.name ?? DEFAULT_SYMBOL,
    [watched, list],
  );
  const symbol = linked ? (active ?? params.symbol ?? fallback) : (params.symbol ?? active ?? fallback);
  const info = list?.find((s) => s.name === symbol);
  const digits = info?.digits ?? 5;

  const change = (patch: Partial<ChartParams>) => {
    api.updateParameters({ ...params, symbol, ...patch });
    prefsStore.set('layout', containerApi.toJSON()); // params are part of the layout
  };

  // A chart that follows Market Watch remembers the symbol it last showed, so a reload opens on the same one.
  useEffect(() => {
    if (linked && active && params.symbol !== active) {
      api.updateParameters({ ...params, symbol: active });
      prefsStore.set('layout', containerApi.toJSON());
    }
  }, [linked, active, params, api, containerApi]);

  const { bars, status, loadOlder, retry } = useBars(symbol, tf);
  const instances = useMemo(() => normalizeInstances(params.indicators), [params.indicators]);
  const rendered = useMemo(() => renderIndicators(instances, bars, tf), [instances, bars, tf]);
  const [editingIndicator, setEditingIndicator] = useState<{
    instance: IndicatorInstance;
    isNew: boolean;
  } | null>(null);
  const [indicatorMenu, setIndicatorMenu] = useState<{ x: number; y: number } | null>(null);
  const trading = useChartTrading(symbol, info);
  const { oneClick: clickTrade } = trading;
  const allDrawings = usePrefsValue((s) => s.data.drawings);
  const savedTemplates = usePrefsValue((s) => s.data.chartTemplates);
  const drawings = useMemo(() => normalizeDrawings(allDrawings?.[symbol]), [allDrawings, symbol]);
  const templates = useMemo(() => normalizeTemplates(savedTemplates), [savedTemplates]);
  const [tool, setTool] = useState<ActiveTool | null>(null);
  const [history, setHistory] = useState<History>({});
  const [toolbarWidth, setToolbarWidth] = useState(0);
  const [more, setMore] = useState<{ x: number; y: number; box: DOMRect } | null>(null);
  const [maximized, setMaximized] = useState(() => api.isMaximized());
  const [controls, setControls] = useState<ChartControls | null>(null);
  const toolbar = useRef<HTMLDivElement>(null);
  const moreButton = useRef<HTMLButtonElement>(null);
  const serverNow = useServerTime();
  const [selected, setSelected] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [drawMenu, setDrawMenu] = useState<{ x: number; y: number } | null>(null);
  const [templateMenu, setTemplateMenu] = useState<{ x: number; y: number } | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const selectedDrawing = drawings.find((d) => d.id === selected) ?? null;

  /** Saves one part of the synced preferences, unless that would overflow what the server accepts. */
  const trySave = (key: 'drawings' | 'chartTemplates', value: Record<string, unknown>): boolean => {
    const next = { ...prefsStore.getState().data, [key]: value };
    if (sizeOf(next) > PREFS_LIMIT_BYTES) {
      trading.setNotice({
        tone: 'error',
        text: 'Your saved chart settings are full. Delete some drawings or templates first.',
      });
      return false;
    }
    prefsStore.set(key, value);
    return true;
  };
  /** Stores this symbol's drawings; `record` keeps the previous list for undo (undo and redo themselves do not). */
  const storeDrawings = (list: Drawing[]) => {
    const rest = { ...(prefsStore.getState().data.drawings ?? {}) };
    if (list.length === 0) delete rest[symbol];
    else rest[symbol] = list;
    return trySave('drawings', rest);
  };
  const saveDrawings = (list: Drawing[]) => {
    const before = drawings;
    const ok = storeDrawings(list);
    if (ok) {
      setHistory((h) => ({
        ...h,
        [symbol]: { undo: pushHistory(h[symbol]?.undo ?? [], before), redo: [] },
      }));
    }
    return ok;
  };
  const undoable = (history[symbol]?.undo.length ?? 0) > 0;
  const redoable = (history[symbol]?.redo.length ?? 0) > 0;
  const undo = () => {
    const h = history[symbol];
    const previous = h?.undo.at(-1);
    if (!h || !previous || !storeDrawings(previous)) return;
    setSelected(null);
    setHistory({ ...history, [symbol]: { undo: h.undo.slice(0, -1), redo: pushHistory(h.redo, drawings) } });
  };
  const redo = () => {
    const h = history[symbol];
    const next = h?.redo.at(-1);
    if (!h || !next || !storeDrawings(next)) return;
    setSelected(null);
    setHistory({ ...history, [symbol]: { undo: pushHistory(h.undo, drawings), redo: h.redo.slice(0, -1) } });
  };
  const editSelected = (patch: Partial<Drawing>) =>
    selectedDrawing &&
    saveDrawings(drawings.map((d) => (d.id === selectedDrawing.id ? { ...d, ...patch } : d)));
  const oneClick = params.oneClick ?? false;
  const acknowledged = usePrefsValue((s) => s.data.oneClickAck === true);
  const [drag, setDrag] = useState<{ line: ChartLine; price: number } | null>(null);
  const [menu, setMenu] = useState<(ContextInfo & {}) | null>(null);
  const [pending, setPending] = useState<{ type: OrderType; price: number } | null>(null);
  const [confirmOneClick, setConfirmOneClick] = useState(false);
  const [confirmClearDrawings, setConfirmClearDrawings] = useState(false);
  const [objectsOpen, setObjectsOpen] = useState(false);
  const defaultVolume = info?.volumeMin ?? 0.01;
  const clickVolume = normalizeVolume(
    Number(params.oneClickVolume ?? defaultVolume),
    info ?? { volumeMin: 0.01, volumeMax: 100, volumeStep: 0.01 },
  );

  /** The phone's quick-trade bar: a tap trades at the market at once, at the volume shown (no ticket, no one-click switch). */
  const quickTrade = (side: 'Buy' | 'Sell') => void clickTrade(side, clickVolume);
  const stepQuickVolume = (direction: 1 | -1) => {
    if (!info) return;
    change({ oneClickVolume: String(normalizeVolume(clickVolume + direction * info.volumeStep, info)) });
  };

  // Alt+B / Alt+S trade at market from the keyboard, only while one-click is on and this chart is the active window.
  useEffect(() => {
    if (!oneClick) return;
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || !api.isActive) return;
      const key = e.key.toLowerCase();
      if (key !== 'b' && key !== 's') return;
      e.preventDefault();
      void clickTrade(key === 'b' ? 'Buy' : 'Sell', clickVolume);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [oneClick, api, clickTrade, clickVolume]);

  // The tab shows what the chart shows.
  useEffect(() => {
    const title = classic ? `${symbol},${tf}` : `${symbol}, ${tf}`;
    if (api.title !== title) api.setTitle(title);
  }, [api, symbol, tf, classic]);

  useEffect(() => {
    const subscription = containerApi.onDidMaximizedGroupChange(() => setMaximized(api.isMaximized()));
    return () => subscription.dispose();
  }, [api, containerApi]);

  // The toolbar keeps to one line: what does not fit goes into the More menu.
  useEffect(() => {
    const el = toolbar.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setToolbarWidth(entry!.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const magnet = params.magnet ?? false;
  const autoScroll = params.autoScroll ?? true;
  const showTrades = params.showTrades ?? true;
  const properties = useMemo(() => normalizeProperties(params.properties), [params.properties]);
  const setProperty = (patch: Partial<typeof properties>) =>
    change({ properties: { ...properties, ...patch } });
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  // MT5's quick navigation: typing on a chart opens a box for a symbol and/or a timeframe ("GBPUSD H4").
  const [navigate, setNavigate] = useState<string | null>(null);
  const [navigateProblem, setNavigateProblem] = useState(false);
  const submitNavigation = () => {
    const target = parseQuickNavigation(
      navigate ?? '',
      (list ?? []).map((s) => s.name),
    );
    if (!target) return setNavigateProblem(true);
    change({
      ...(target.symbol ? { symbol: target.symbol, linked: false } : {}),
      ...(target.tf ? { tf: target.tf } : {}),
    });
    setNavigate(null);
    setNavigateProblem(false);
  };

  const quote = useStream((s) => s.quotes[symbol]);
  const themeColors = useMemo(
    () => readChartColors(host ?? document.documentElement),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the palette changes only with the theme
    [host, theme],
  );
  // The chart's own colours (F8) on top of the theme's.
  const ownColors = properties.colors;
  const colors = useMemo(() => chartColorsWith(themeColors, ownColors), [themeColors, ownColors]);
  const separators = useMemo(
    () => (properties.periodSeparators ? periodStarts(bars, tf) : []),
    // Recomputed when bars are added, not on every tick of the live bar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [properties.periodSeparators, tf, bars.length, bars[0]?.t],
  );
  const { markers, links } = useTradeMarkers(symbol, bars, colors, showTrades);
  const crosshair = hovered?.key === `${symbol}|${tf}` ? hovered.bar : null;

  const shownBar = crosshair ?? bars[bars.length - 1] ?? null;
  const legend = shownBar ? legendOf(shownBar) : null;
  // Indicator values on the bar shown in the legend: on the chips, and in the Data Window for the chart the cursor is on.
  const values = useMemo(
    () => (shownBar ? readout(rendered, shownBar.t, digits) : []),
    [rendered, shownBar, digits],
  );
  const publishData = useDataWindow((s) => s.publish);
  useEffect(() => {
    if (shownBar && (crosshair || api.isActive))
      publishData({ symbol, tf, digits, bar: shownBar, indicators: values });
  }, [crosshair, shownBar, values, symbol, tf, digits, api, publishData]);
  const selectClass =
    'rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent';

  /** Saves the chart as a PNG, with a header naming the symbol, timeframe, last prices and server time. */
  const saveImage = () => {
    const shot = controls?.screenshot();
    if (!shot || !host) return;
    const scale = shot.width / Math.max(1, host.clientWidth);
    const header = Math.round(26 * scale);
    const canvas = document.createElement('canvas');
    canvas.width = shot.width;
    canvas.height = shot.height + header;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = colors.panel;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(shot, 0, header);
    ctx.fillStyle = colors.text;
    ctx.font = `${Math.round(13 * scale)}px sans-serif`;
    const last = bars[bars.length - 1];
    const prices = last
      ? `  O ${fmt(last.o, digits)}  H ${fmt(last.h, digits)}  L ${fmt(last.l, digits)}  C ${fmt(last.c, digits)}`
      : '';
    const clock = serverNow ? `  ·  server ${formatServerTime(serverNow)}` : '';
    ctx.fillText(`${symbol}, ${tf}${prices}${clock}`, Math.round(8 * scale), Math.round(18 * scale));
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = screenshotName(symbol, tf, new Date());
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
    }, 'image/png');
  };

  const toggleMaximize = () => (api.isMaximized() ? api.exitMaximized() : api.maximize());

  /**
   * Moves this chart into a window of its own, to put on another monitor. Closing that window brings the chart back. The
   * new window is given the page's theme, since dockview copies the styles but not the root element's attributes.
   */
  const popOut = async () => {
    const panel = containerApi.getPanel(api.id);
    if (!panel) return;
    const opened = await containerApi.addPopoutGroup(panel, {
      popoutUrl: '/popout.html',
      onDidOpen: ({ window: popup }) => {
        const root = document.documentElement;
        for (const name of root.getAttributeNames()) {
          popup.document.documentElement.setAttribute(name, root.getAttribute(name) ?? '');
        }
        popup.document.title = `${symbol}, ${tf}`;
      },
    });
    if (!opened) {
      trading.setNotice({
        tone: 'error',
        text: 'The browser blocked the new window. Allow pop-ups for this site and try again.',
      });
    }
  };

  // Keyboard shortcuts while this chart is the active panel (and no field has the keyboard).
  const shortcuts = useRef<(e: KeyboardEvent) => void>(() => undefined);
  const handleShortcut = (e: KeyboardEvent) => {
    if (!api.isActive) return;
    const target = e.target as HTMLElement | null;
    if (target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable)) return;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && !e.altKey && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
      return;
    }
    // MT5: Ctrl+G grid, Ctrl+L volumes, Ctrl+Y period separators (redo is Ctrl+Shift+Z)
    if (mod && !e.altKey && !e.shiftKey) {
      const key = e.key.toLowerCase();
      const toggle = key === 'g' ? 'grid' : key === 'l' ? 'volumes' : key === 'y' ? 'periodSeparators' : null;
      if (toggle) {
        e.preventDefault();
        setProperty({ [toggle]: !properties[toggle] });
        return;
      }
    }
    // Alt+1 bars, Alt+2 candles, Alt+3 line
    if (e.altKey && !mod && ['1', '2', '3'].includes(e.key)) {
      e.preventDefault();
      change({ type: e.key === '1' ? 'bars' : e.key === '2' ? 'candles' : 'line' });
      return;
    }
    if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      setObjectsOpen(true);
      return;
    }
    if (e.key === 'F8' && !mod && !e.altKey) {
      e.preventDefault();
      setPropertiesOpen(true);
      return;
    }
    if (e.key === 'F12' && !mod && !e.altKey) {
      e.preventDefault();
      if (autoScroll) change({ autoScroll: false }); // stepping means looking at the past
      controls?.step();
      return;
    }
    if (mod || e.altKey) return;
    // Letters (or Enter) start the quick navigation box, when nothing else on the page has the keyboard
    const free = !target || target === document.body || (host?.contains(target) ?? false);
    if (free && (/^[a-zA-Z]$/.test(e.key) || e.key === 'Enter')) {
      e.preventDefault();
      setNavigate(e.key === 'Enter' ? '' : e.key.toUpperCase());
      setNavigateProblem(false);
      return;
    }
    const next = timeframeForKey(e.key);
    if (next) {
      e.preventDefault();
      change({ tf: next });
    } else if (e.key === '+' || e.key === '=') {
      controls?.zoom(1);
    } else if (e.key === '-') {
      controls?.zoom(-1);
    } else if (e.key === 'End') {
      controls?.toLatest();
    }
  };
  useEffect(() => {
    shortcuts.current = handleShortcut;
  });
  const onShortcut = useCallback((e: KeyboardEvent) => shortcuts.current(e), []);
  useEffect(() => {
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, [onShortcut]);

  // The active chart offers its commands to the main toolbar and the menu bar.
  const commands = useRef<Omit<ChartCommands, 'id' | 'state'>>(null!);
  useEffect(() => {
    commands.current = {
      setSymbol: (next) => change({ symbol: next, linked: false }),
      setType: (next) => change({ type: next }),
      setTimeframe: (next) => change({ tf: next }),
      zoom: (direction) => controls?.zoom(direction),
      toLatest: () => controls?.toLatest(),
      step: () => {
        if (autoScroll) change({ autoScroll: false });
        controls?.step();
      },
      toggle: (what) => {
        if (what === 'autoScroll') return change({ autoScroll: !autoScroll });
        if (what === 'oneClick') {
          if (oneClick) return change({ oneClick: false });
          if (acknowledged) return change({ oneClick: true });
          return setConfirmOneClick(true);
        }
        setProperty({ [what]: !properties[what] });
      },
      setTool: (next) => {
        setSelected(null);
        setTool(next);
      },
      addIndicator: (id) => {
        const def = indicatorById(id);
        if (def) setEditingIndicator({ instance: newInstance(def), isNew: true });
      },
      applyTemplate: (name) => {
        const template = templates[name];
        if (!template) return;
        const { patch, drawings: templateDrawings } = applyTemplate(template);
        change({ ...patch });
        if (templateDrawings) saveDrawings(templateDrawings);
      },
      saveTemplate: () => setSavingTemplate(true),
      openProperties: () => setPropertiesOpen(true),
      openObjects: () => setObjectsOpen(true),
      openIndicatorList: () => {
        const box = host?.getBoundingClientRect();
        setIndicatorMenu({ x: (box?.left ?? 0) + 8, y: (box?.top ?? 0) + 8 });
      },
      saveImage,
      popOut: () => void popOut(),
    };
  });
  const [isActive, setIsActive] = useState(api.isActive);
  useEffect(() => {
    const subscription = api.onDidActiveChange((e) => setIsActive(e.isActive));
    return () => subscription.dispose();
  }, [api]);
  const register = useChartCommands((s) => s.register);
  const unregister = useChartCommands((s) => s.unregister);
  const drawingTool = tool;
  useEffect(() => {
    // The active chart takes the commands and keeps them until another chart becomes active; a restored layout sends no
    // "became active" event, so a chart also takes them while no chart holds them.
    const holder = useChartCommands.getState().active;
    if (!isActive && holder && holder.id !== api.id) return;
    const call =
      <K extends keyof Omit<ChartCommands, 'id' | 'state'>>(key: K) =>
      (...args: Parameters<ChartCommands[K]>) =>
        (commands.current[key] as (...a: Parameters<ChartCommands[K]>) => void)(...args);
    register({
      id: api.id,
      state: {
        symbol,
        tf,
        type,
        grid: properties.grid,
        volumes: properties.volumes,
        periodSeparators: properties.periodSeparators,
        autoScroll,
        chartShift: properties.chartShift,
        oneClick,
        tool: drawingTool,
      },
      setSymbol: call('setSymbol'),
      setType: call('setType'),
      setTimeframe: call('setTimeframe'),
      zoom: call('zoom'),
      toLatest: call('toLatest'),
      step: call('step'),
      toggle: call('toggle'),
      setTool: call('setTool'),
      addIndicator: call('addIndicator'),
      applyTemplate: call('applyTemplate'),
      saveTemplate: call('saveTemplate'),
      openProperties: call('openProperties'),
      openObjects: call('openObjects'),
      openIndicatorList: call('openIndicatorList'),
      saveImage: call('saveImage'),
      popOut: call('popOut'),
    });
  }, [
    isActive,
    register,
    api.id,
    symbol,
    tf,
    type,
    properties.grid,
    properties.volumes,
    properties.periodSeparators,
    properties.chartShift,
    autoScroll,
    oneClick,
    drawingTool,
  ]);
  useEffect(() => () => unregister(api.id), [api.id, unregister]);

  const allActions: ToolbarAction[] = [
    {
      id: 'draw',
      icon: 'draw',
      label: `Draw${drawings.length > 0 ? ` (${drawings.length})` : ''}`,
      pressed: tool !== null && tool !== 'measure',
      menu: true,
      onSelect: (box) => setDrawMenu({ x: box.left, y: box.bottom + 4 }),
    },
    {
      id: 'indicators',
      icon: 'indicators',
      label: `Indicators${instances.length > 0 ? ` (${instances.length})` : ''}`,
      menu: true,
      onSelect: (box) => setIndicatorMenu({ x: box.left, y: box.bottom + 4 }),
    },
    {
      id: 'templates',
      icon: 'templates',
      label: 'Templates',
      menu: true,
      onSelect: (box) => setTemplateMenu({ x: box.left, y: box.bottom + 4 }),
    },
    {
      id: 'oneClick',
      icon: 'oneClick',
      label: `One-click trading ${oneClick ? 'ON' : 'off'}`,
      shortcut: 'Alt+B / Alt+S',
      pressed: oneClick,
      onSelect: () => {
        if (oneClick) return change({ oneClick: false });
        if (acknowledged) return change({ oneClick: true });
        setConfirmOneClick(true);
      },
    },
    {
      id: 'undo',
      icon: 'undo',
      label: 'Undo drawing change',
      shortcut: 'Ctrl+Z',
      disabled: !undoable,
      onSelect: undo,
    },
    {
      id: 'measure',
      icon: 'measure',
      label: 'Measure',
      pressed: tool === 'measure',
      onSelect: () => {
        setSelected(null);
        setTool(tool === 'measure' ? null : 'measure');
      },
    },
    {
      id: 'magnet',
      icon: 'magnet',
      label: 'Snap drawings to prices',
      pressed: magnet,
      onSelect: () => change({ magnet: !magnet }),
    },
    { id: 'image', icon: 'camera', label: 'Save as image', onSelect: saveImage },
    {
      id: 'properties',
      icon: 'properties',
      label: 'Properties',
      shortcut: 'F8',
      onSelect: () => setPropertiesOpen(true),
    },
    ...(mobile
      ? []
      : [
          {
            id: 'popout',
            icon: 'popout' as const,
            label: 'Pop out to a new window (another monitor)',
            onSelect: () => void popOut(),
          },
        ]),
    {
      id: 'maximize',
      icon: maximized ? 'restore' : 'maximize',
      label: maximized ? 'Restore panel size' : 'Maximise panel',
      onSelect: toggleMaximize,
    },
    {
      id: 'zoomIn',
      icon: 'zoomIn',
      label: 'Zoom in',
      shortcut: '+',
      onSelect: () => controls?.zoom(1),
    },
    {
      id: 'zoomOut',
      icon: 'zoomOut',
      label: 'Zoom out',
      shortcut: '−',
      onSelect: () => controls?.zoom(-1),
    },
    {
      id: 'latest',
      icon: 'latest',
      label: 'Go to the latest bar',
      shortcut: 'End',
      onSelect: () => controls?.toLatest(),
    },
    {
      id: 'autoScroll',
      icon: 'autoScroll',
      label: 'Auto-scroll to new bars',
      pressed: autoScroll,
      onSelect: () => change({ autoScroll: !autoScroll }),
    },
    {
      id: 'trades',
      icon: 'trades',
      label: 'Show past trades',
      pressed: showTrades,
      onSelect: () => change({ showTrades: !showTrades }),
    },
    {
      id: 'bidAsk',
      icon: 'bidAsk',
      label: 'Bid/Ask lines',
      pressed: bidAsk,
      onSelect: () => change({ bidAsk: !bidAsk }),
    },
    {
      id: 'linked',
      icon: 'link',
      label: 'Follow Market Watch',
      pressed: linked,
      onSelect: () => change({ linked: !linked }),
    },
  ];
  // On a phone the chart already fills the screen: there is nothing to maximise.
  const actions = mobile ? allActions.filter((a) => a.id !== 'maximize') : allActions;
  const fit = toolbarFit(toolbarWidth || 1_000, actions.length);
  const inline = actions.slice(0, fit.icons);
  const overflow = actions.slice(fit.icons);

  const menuItems = (): MenuItem[] => {
    if (!menu) return [];
    const items: MenuItem[] = [];
    const line = menu.line;
    const price = menu.price;
    const can = trading.online;
    if (line) {
      if (
        line.kind === 'positionSl' ||
        line.kind === 'positionTp' ||
        line.kind === 'orderSl' ||
        line.kind === 'orderTp'
      ) {
        items.push({
          label: `Remove ${line.kind.endsWith('Sl') ? 'stop loss' : 'take profit'} of #${line.ticket}`,
          disabled: !can,
          onSelect: () => void trading.removeLevel(line),
        });
      } else if (line.kind === 'position') {
        items.push({
          label: `Close position #${line.ticket}`,
          disabled: !can,
          danger: true,
          onSelect: () => void trading.closePosition(line.ticket),
        });
      } else {
        items.push({
          label: `Delete order #${line.ticket}`,
          disabled: !can,
          danger: true,
          onSelect: () => void trading.cancelOrder(line.ticket),
        });
      }
      return items;
    }
    if (price !== null && trading.quote) {
      for (const p of trading.positions.filter((x) => x.symbol === symbol)) {
        const target = stopTargetAt(p, price);
        items.push({
          label: `Set ${target === 'stopLoss' ? 'stop loss' : 'take profit'} of #${p.ticket} here`,
          disabled: !can,
          onSelect: () => void trading.setStopAt(p.ticket, target, price),
        });
      }
      for (const type of pendingChoicesAt(price, trading.quote.bid, trading.quote.ask)) {
        items.push({
          label: `${KIND_LABELS[type as keyof typeof KIND_LABELS]} at ${price.toFixed(digits)}…`,
          disabled: trading.reason !== null,
          onSelect: () => setPending({ type, price: snapPrice(price, trading.precision) }),
        });
      }
    }
    items.push({
      label: 'Open order ticket (F9)',
      onSelect: () => {
        useWorkspace.getState().select(symbol);
        useWorkspace.getState().actions?.orderTicket();
      },
    });
    return items;
  };

  return (
    <section
      className="relative flex h-full flex-col bg-panel"
      aria-label={`Chart ${symbol} ${tf}`}
      data-bar-count={bars.length}
      data-last-bar={bars[bars.length - 1]?.t ?? ''}
    >
      <div
        ref={toolbar}
        role="toolbar"
        aria-label="Chart tools"
        hidden={classic}
        className="flex flex-nowrap items-center gap-1.5 overflow-hidden border-b border-line px-2 py-1.5"
      >
        <div className="w-24 shrink-0">
          <label className="sr-only" htmlFor={`${api.id}-symbol`}>
            Symbol
          </label>
          <SymbolPicker
            id={`${api.id}-symbol`}
            className={`${selectClass} w-full`}
            value={symbol}
            options={list ?? [{ name: symbol }]}
            onSelect={(name) => change({ symbol: name, linked: false })}
          />
        </div>
        {fit.timeframeSelect ? (
          <select
            aria-label="Timeframe"
            className={`${selectClass} shrink-0`}
            value={tf}
            onChange={(e) => change({ tf: e.target.value as Timeframe })}
            title="Timeframe (keys 1 to 9)"
          >
            {TIMEFRAMES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        ) : (
          <div
            role="group"
            aria-label="Timeframe"
            className="flex shrink-0 overflow-hidden rounded-md border border-line"
          >
            {STANDARD_TIMEFRAMES.map((t, i) => (
              <button
                key={t}
                type="button"
                aria-pressed={t === tf}
                onClick={() => change({ tf: t })}
                title={`${t} (key ${i + 1})`}
                className={`px-1.5 py-1 text-xs font-semibold ${t === tf ? 'bg-accent text-accent-fg' : 'hover:bg-panel2'}`}
              >
                {t}
              </button>
            ))}
            <select
              aria-label="More timeframes"
              title="The other timeframes"
              value={(STANDARD_TIMEFRAMES as readonly string[]).includes(tf) ? '' : tf}
              onChange={(e) => e.target.value && change({ tf: e.target.value as Timeframe })}
              className={`w-11 border-l border-line bg-transparent px-0.5 text-xs font-semibold ${
                (STANDARD_TIMEFRAMES as readonly string[]).includes(tf)
                  ? 'text-muted'
                  : 'bg-accent text-accent-fg'
              }`}
            >
              <option value="">…</option>
              {TIMEFRAMES.filter((t) => !(STANDARD_TIMEFRAMES as readonly string[]).includes(t)).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="sr-only" htmlFor={`${api.id}-type`}>
          Chart type
        </label>
        <select
          id={`${api.id}-type`}
          className={`${selectClass} shrink-0`}
          value={type}
          onChange={(e) => change({ type: e.target.value as ChartType })}
        >
          {CHART_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <div className="flex shrink-0 items-center gap-0.5">
          {inline.map((a) => (
            <IconButton key={a.id} action={a} />
          ))}
        </div>
        {overflow.length > 0 && (
          <button
            ref={moreButton}
            type="button"
            aria-haspopup="menu"
            aria-expanded={more !== null}
            aria-label="More chart tools"
            title="More chart tools"
            onClick={() => {
              const box = moreButton.current!.getBoundingClientRect();
              setMore({ x: box.right - 220, y: box.bottom + 4, box });
            }}
            className="ml-auto shrink-0 rounded-md border border-line p-[5px] hover:border-accent"
          >
            <Icon name="more" />
          </button>
        )}
      </div>

      {mobile && (
        // The phone's quick-trade bar, as on MetaTrader's phone chart: Sell, the volume to trade, Buy. A tap trades at the
        // market right away at that volume.
        <div
          className="grid grid-cols-[1fr_auto_1fr] items-stretch border-b border-line"
          role="group"
          aria-label="Quick trade"
          data-testid="quick-trade-bar"
        >
          <button
            type="button"
            disabled={trading.busy || trading.reason !== null || !sideAllowed(info?.tradeMode, 'Sell')}
            onClick={() => quickTrade('Sell')}
            aria-label="Sell"
            className="mt5-sell flex flex-col items-start px-3 py-1.5 text-left text-white disabled:opacity-40"
          >
            <span className="text-[10px] uppercase leading-none">Sell</span>
            <span className="num text-xl leading-tight">{quote ? quote.bid.toFixed(digits) : '—'}</span>
          </button>
          <div className="flex items-center gap-1 px-2">
            <button
              type="button"
              aria-label="Decrease volume"
              onClick={() => stepQuickVolume(-1)}
              className="grid h-10 w-9 place-items-center text-muted"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <label className="sr-only" htmlFor={`${api.id}-quick-volume`}>
              Volume
            </label>
            <input
              id={`${api.id}-quick-volume`}
              inputMode="decimal"
              className="num w-16 bg-transparent text-center text-lg outline-none"
              value={params.oneClickVolume ?? String(defaultVolume)}
              onChange={(e) => change({ oneClickVolume: e.target.value })}
            />
            <button
              type="button"
              aria-label="Increase volume"
              onClick={() => stepQuickVolume(1)}
              className="grid h-10 w-9 place-items-center text-muted"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 15l6-6 6 6" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            disabled={trading.busy || trading.reason !== null || !sideAllowed(info?.tradeMode, 'Buy')}
            onClick={() => quickTrade('Buy')}
            aria-label="Buy"
            className="mt5-buy flex flex-col items-end px-3 py-1.5 text-right text-white disabled:opacity-40"
          >
            <span className="text-[10px] uppercase leading-none">Buy</span>
            <span className="num text-xl leading-tight">{quote ? quote.ask.toFixed(digits) : '—'}</span>
          </button>
        </div>
      )}

      <div
        className={`chart-surface relative min-h-0 flex-1 ${oneClick && !classic ? 'ring-2 ring-inset ring-accent' : ''}`}
        ref={setHost}
        data-one-click={oneClick}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('text/plain')) e.preventDefault(); // allows the drop below
        }}
        onDrop={(e) => {
          const name = e.dataTransfer.getData('text/plain');
          if (!name || !list?.some((s) => s.name === name)) return;
          e.preventDefault();
          change({ symbol: name, linked: false });
        }}
      >
        {legend && shownBar && (
          <div
            className="num pointer-events-none absolute left-2 top-1 z-10 flex flex-wrap gap-x-3 text-xs"
            aria-live="off"
            data-testid="chart-legend"
          >
            <span className={`font-semibold ${mobile ? 'w-full' : ''}`}>
              {symbol}, {tf}
              {/* On a phone the title carries the instrument's name too, on its own line, as MetaTrader's phone chart does. */}
              {mobile && info?.description ? `: ${info.description}` : ''}
            </span>
            <span>O {fmt(legend.o, digits)}</span>
            <span>H {fmt(legend.h, digits)}</span>
            <span>L {fmt(legend.l, digits)}</span>
            <span>C {fmt(legend.c, digits)}</span>
            <span className={legend.change > 0 ? 'text-up' : legend.change < 0 ? 'text-down' : 'text-muted'}>
              {legend.change > 0 ? '+' : ''}
              {fmt(legend.change, digits)} ({legend.changePct.toFixed(2)}%)
            </span>
          </div>
        )}
        {mobile && (
          // Round zoom out / zoom in / back-to-latest buttons floating over the chart, as on MetaTrader's phone chart.
          <div
            className="absolute bottom-3 right-16 z-20 flex gap-3"
            role="group"
            aria-label="Chart zoom"
            data-testid="chart-zoom-buttons"
          >
            {(
              [
                { label: 'Zoom out', glyph: '−', run: () => controls?.zoom(-1) },
                { label: 'Zoom in', glyph: '+', run: () => controls?.zoom(1) },
                { label: 'Go to the latest bar', glyph: '↺', run: () => controls?.toLatest() },
              ] as const
            ).map((b) => (
              <button
                key={b.label}
                type="button"
                aria-label={b.label}
                onClick={b.run}
                className="grid h-11 w-11 place-items-center rounded-full border border-line bg-panel text-2xl leading-none text-muted shadow-lg active:bg-panel2"
              >
                {b.glyph}
              </button>
            ))}
          </div>
        )}
        <ChartView
          bars={bars}
          type={type}
          digits={digits}
          colors={colors}
          bid={quote?.bid ?? null}
          ask={quote?.ask ?? null}
          showBidAsk={bidAsk && quote !== undefined}
          onNeedOlder={() => void loadOlder()}
          onCrosshair={(bar) => setHovered(bar ? { key: `${symbol}|${tf}`, bar } : null)}
          indicators={rendered}
          drawings={drawings}
          selectedDrawing={selected}
          drawingTool={tool}
          drawingColor={colors.accent}
          magnet={magnet}
          autoScroll={autoScroll}
          markers={markers}
          links={links}
          linkColors={colors}
          properties={properties}
          background={colors.background}
          separators={separators}
          onControls={setControls}
          barSeconds={timeframeSeconds(tf)}
          hasKeyboard={() => api.isActive}
          drawingHandlers={{
            onChange: saveDrawings,
            onSelect: setSelected,
            onToolDone: () => setTool(null),
            onHint: setHint,
          }}
          lines={properties.tradeLevels ? trading.lines : NO_LINES}
          editable={trading.online}
          precision={trading.precision}
          lineHandlers={{
            onDragMove: (line, price) => setDrag(line && price !== null ? { line, price } : null),
            onDragEnd: trading.dropLine,
            onContextMenu: setMenu,
          }}
        />

        {instances.length > 0 && (
          <ul
            className="absolute left-2 top-14 z-10 flex max-w-[70%] flex-wrap gap-1 text-xs"
            aria-label="Indicators on this chart"
          >
            {instances.map((inst) => {
              const def = indicatorById(inst.type);
              return (
                <li
                  key={inst.uid}
                  className="flex items-center overflow-hidden rounded border border-line bg-panel2/90"
                >
                  <button
                    type="button"
                    className="px-2 py-0.5 hover:bg-panel"
                    onClick={() => setEditingIndicator({ instance: inst, isNew: false })}
                    aria-label={`Edit ${def?.title(inst.params) ?? inst.type}`}
                  >
                    {values.find((v) => v.uid === inst.uid)?.title ?? def?.title(inst.params) ?? inst.type}
                    {values
                      .find((v) => v.uid === inst.uid)
                      ?.values.filter((v) => v.value !== null)
                      .map((v) => (
                        <span key={v.label} className="num ml-1.5" style={{ color: v.color }} title={v.label}>
                          {v.text}
                        </span>
                      ))}
                  </button>
                  <button
                    type="button"
                    className="px-1.5 py-0.5 text-muted hover:text-down"
                    aria-label={`Remove ${def?.title(inst.params) ?? inst.type}`}
                    onClick={() => change({ indicators: instances.filter((i) => i.uid !== inst.uid) })}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {hint && (
          <p
            role="status"
            className="pointer-events-none absolute left-1/2 top-2 z-20 -translate-x-1/2 rounded-md border border-accent bg-panel2/95 px-3 py-1 text-xs shadow-lg"
            data-testid="drawing-hint"
          >
            {hint}
          </p>
        )}
        {selectedDrawing && (
          <div
            role="group"
            aria-label="Drawing settings"
            className="absolute right-16 top-2 z-20 flex flex-wrap items-center gap-2 rounded-md border border-line bg-panel2/95 p-2 text-xs shadow-lg"
          >
            <span className="font-semibold">{TOOL_LABELS[selectedDrawing.tool]}</span>
            <input
              type="color"
              aria-label="Drawing color"
              value={selectedDrawing.color}
              onChange={(e) => editSelected({ color: e.target.value })}
              className="h-6 w-8 cursor-pointer rounded border border-line bg-transparent"
            />
            <select
              aria-label="Drawing line width"
              className="rounded border border-line bg-panel px-1 py-0.5"
              value={selectedDrawing.width}
              onChange={(e) => editSelected({ width: Number(e.target.value) as 1 | 2 | 3 })}
            >
              {[1, 2, 3].map((w) => (
                <option key={w} value={w}>
                  {w}px
                </option>
              ))}
            </select>
            <select
              aria-label="Drawing line style"
              className="rounded border border-line bg-panel px-1 py-0.5"
              value={selectedDrawing.style}
              onChange={(e) => editSelected({ style: e.target.value as Drawing['style'] })}
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
            {EXTENDABLE.includes(selectedDrawing.tool) && (
              <select
                aria-label="Extend"
                className="rounded border border-line bg-panel px-1 py-0.5"
                value={selectedDrawing.extend}
                onChange={(e) => editSelected({ extend: e.target.value as Drawing['extend'] })}
              >
                <option value="none">No extension</option>
                <option value="right">Extend right</option>
                <option value="both">Extend both ways</option>
              </select>
            )}
            {selectedDrawing.tool === 'text' && (
              <input
                aria-label="Label text"
                className="w-32 rounded border border-line bg-panel px-1 py-0.5"
                maxLength={80}
                value={selectedDrawing.text ?? ''}
                onChange={(e) => editSelected({ text: e.target.value })}
              />
            )}
            <button
              type="button"
              className="rounded border border-down px-2 py-0.5 text-down hover:bg-down/10"
              onClick={() => {
                saveDrawings(drawings.filter((d) => d.id !== selectedDrawing.id));
                setSelected(null);
              }}
            >
              Delete
            </button>
          </div>
        )}
        {drag && (
          <DragReadout
            drag={drag}
            positions={trading.positions}
            orders={trading.orders}
            symbol={symbol}
            digits={digits}
            login={trading.login}
            balance={trading.account?.figures.balance ?? 0}
            currency={trading.account?.currency ?? ''}
          />
        )}
        {oneClick && !mobile && (
          <div
            className="absolute left-2 top-7 z-20 flex items-stretch gap-1 rounded-md border border-accent bg-panel2/95 p-1 text-xs shadow-lg"
            role="group"
            aria-label="One-click trading"
          >
            <button
              type="button"
              disabled={trading.busy || trading.reason !== null || !sideAllowed(info?.tradeMode, 'Sell')}
              onClick={() => void clickTrade('Sell', clickVolume)}
              className="mt5-sell num rounded px-3 py-1 font-semibold text-white disabled:opacity-40"
              aria-label="One-click sell"
            >
              Sell {quote ? quote.bid.toFixed(digits) : '—'}
            </button>
            <label className="sr-only" htmlFor={`${api.id}-oc-volume`}>
              One-click volume
            </label>
            <input
              id={`${api.id}-oc-volume`}
              className="num w-16 rounded border border-line bg-panel px-1 text-center outline-none focus:border-accent"
              inputMode="decimal"
              value={params.oneClickVolume ?? String(defaultVolume)}
              onChange={(e) => change({ oneClickVolume: e.target.value })}
            />
            <button
              type="button"
              disabled={trading.busy || trading.reason !== null || !sideAllowed(info?.tradeMode, 'Buy')}
              onClick={() => void clickTrade('Buy', clickVolume)}
              className="mt5-buy num rounded px-3 py-1 font-semibold text-white disabled:opacity-40"
              aria-label="One-click buy"
            >
              Buy {quote ? quote.ask.toFixed(digits) : '—'}
            </button>
          </div>
        )}
        {trading.notice && (
          <p
            role={trading.notice.tone === 'error' ? 'alert' : 'status'}
            className={
              trading.notice.tone === 'error'
                ? 'absolute bottom-2 left-2 z-20 max-w-[80%] rounded-md border border-down bg-panel2 px-3 py-1 text-xs text-down'
                : 'sr-only'
            }
            data-testid="chart-notice"
            onClick={() => trading.setNotice(null)}
          >
            {trading.notice.text}
          </p>
        )}
        {status === 'loading' && (
          <p
            role="status"
            className="absolute inset-0 z-10 grid place-items-center bg-panel/70 text-sm text-muted"
          >
            Loading bars…
          </p>
        )}
        {status === 'error' && (
          <div
            role="alert"
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-panel/70 text-sm text-muted"
          >
            <span>Could not load the chart.</span>
            <button
              type="button"
              className="rounded-md border border-line bg-panel2 px-3 py-1 text-sm text-fg hover:border-accent"
              onClick={retry}
            >
              Retry
            </button>
          </div>
        )}
        {status === 'ready' && bars.length === 0 && (
          <p className="absolute inset-0 z-10 grid place-items-center text-sm text-muted">
            No history for {symbol} on {tf} yet.
          </p>
        )}
      </div>

      {more && (
        <ContextMenu
          x={more.x}
          y={more.y}
          title="More chart tools"
          items={overflow.map((a) => ({
            key: a.id,
            label: a.menu ? `${a.label}…` : a.label,
            shortcut: a.shortcut,
            checked: a.pressed,
            disabled: a.disabled,
            onSelect: () => a.onSelect(more.box),
          }))}
          onClose={() => setMore(null)}
        />
      )}
      {drawMenu && (
        <ContextMenu
          x={drawMenu.x}
          y={drawMenu.y}
          title="Drawing tools"
          items={[
            ...TOOLS.map((t) => ({
              label: TOOL_LABELS[t],
              onSelect: () => {
                setSelected(null);
                setTool(t);
              },
            })),
            {
              label: 'Redo drawing change',
              shortcut: 'Ctrl+Shift+Z',
              disabled: !redoable,
              onSelect: redo,
            },
            {
              label: `Objects list (${drawings.length})`,
              shortcut: 'Ctrl+B',
              onSelect: () => setObjectsOpen(true),
            },
            {
              label: `Delete all drawings on ${symbol}`,
              disabled: drawings.length === 0,
              danger: true,
              onSelect: () => setConfirmClearDrawings(true),
            },
          ]}
          onClose={() => setDrawMenu(null)}
        />
      )}
      {templateMenu && (
        <ContextMenu
          x={templateMenu.x}
          y={templateMenu.y}
          title="Chart templates"
          items={[
            { label: 'Save this chart as a template…', onSelect: () => setSavingTemplate(true) },
            ...Object.keys(templates).flatMap((name) => [
              {
                label: `Apply: ${name}`,
                onSelect: () => {
                  const { patch, drawings: templateDrawings } = applyTemplate(templates[name]!);
                  change({ ...patch });
                  if (templateDrawings) saveDrawings(templateDrawings);
                },
              },
              {
                label: `Delete template: ${name}`,
                danger: true,
                onSelect: () => {
                  const rest = { ...(prefsStore.getState().data.chartTemplates ?? {}) };
                  delete rest[name];
                  trySave('chartTemplates', rest);
                },
              },
            ]),
          ]}
          onClose={() => setTemplateMenu(null)}
        />
      )}
      <Modal open={savingTemplate} title="Save chart template" onClose={() => setSavingTemplate(false)}>
        {savingTemplate && (
          <TemplateForm
            symbol={symbol}
            templates={templates}
            hasDrawings={drawings.length > 0}
            onSave={(name, includeDrawings, includeTimeframe) => {
              const template = makeTemplate(
                { type, tf, bidAsk, indicators: instances, properties },
                includeDrawings ? drawings : null,
                includeTimeframe,
              );
              const ok = trySave('chartTemplates', {
                ...(prefsStore.getState().data.chartTemplates ?? {}),
                [name]: template,
              });
              if (ok) setSavingTemplate(false);
              return ok;
            }}
            onClose={() => setSavingTemplate(false)}
          />
        )}
      </Modal>
      {indicatorMenu && (
        <ContextMenu
          x={indicatorMenu.x}
          y={indicatorMenu.y}
          title="Add indicator"
          searchable
          items={INDICATORS.map((def) => ({
            label: `${def.group}: ${def.name}`,
            onSelect: () => setEditingIndicator({ instance: newInstance(def), isNew: true }),
          }))}
          onClose={() => setIndicatorMenu(null)}
        />
      )}
      <ObjectsList
        open={objectsOpen}
        symbol={symbol}
        drawings={drawings}
        digits={digits}
        onSelect={(id) => {
          setTool(null);
          setSelected(id);
        }}
        onDelete={(id) => {
          saveDrawings(drawings.filter((d) => d.id !== id));
          if (selected === id) setSelected(null);
        }}
        onDeleteAll={() => {
          setObjectsOpen(false);
          setConfirmClearDrawings(true);
        }}
        onClose={() => setObjectsOpen(false)}
      />
      <ChartPropertiesDialog
        open={propertiesOpen}
        title={`${symbol}, ${tf}`}
        settings={{ properties, autoScroll, showTrades, bidAsk }}
        theme={themeColors}
        onSave={(next) =>
          change({
            properties: next.properties,
            autoScroll: next.autoScroll,
            showTrades: next.showTrades,
            bidAsk: next.bidAsk,
          })
        }
        onClose={() => setPropertiesOpen(false)}
      />
      {navigate !== null && (
        <form
          className="absolute bottom-8 left-2 z-30 flex items-center gap-1 rounded-md border border-accent bg-panel2 p-1 shadow-lg"
          onSubmit={(e) => {
            e.preventDefault();
            submitNavigation();
          }}
        >
          <label className="sr-only" htmlFor={`${api.id}-navigate`}>
            Symbol or timeframe
          </label>
          <input
            id={`${api.id}-navigate`}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            value={navigate}
            placeholder="EURUSD H1"
            aria-invalid={navigateProblem}
            onChange={(e) => {
              setNavigate(e.target.value);
              setNavigateProblem(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setNavigate(null);
              }
            }}
            onBlur={() => setNavigate(null)}
            className={`w-36 rounded bg-panel px-2 py-0.5 text-sm uppercase outline-none ${navigateProblem ? 'text-down' : ''}`}
          />
          {navigateProblem && (
            <span role="alert" className="px-1 text-xs text-down">
              No such symbol or timeframe
            </span>
          )}
        </form>
      )}
      <IndicatorDialog
        editing={editingIndicator}
        hasEarlier={
          editingIndicator !== null &&
          (editingIndicator.isNew
            ? instances.length > 0
            : instances.findIndex((i) => i.uid === editingIndicator.instance.uid) > 0)
        }
        onSave={(saved) =>
          change({
            indicators: instances.some((i) => i.uid === saved.uid)
              ? instances.map((i) => (i.uid === saved.uid ? saved : i))
              : [...instances, saved],
          })
        }
        onRemove={(uid) => change({ indicators: instances.filter((i) => i.uid !== uid) })}
        onClose={() => setEditingIndicator(null)}
      />
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={menu.price !== null ? `${symbol} at ${menu.price.toFixed(digits)}` : symbol}
          items={menuItems()}
          onClose={() => setMenu(null)}
        />
      )}
      <PendingConfirm
        request={pending}
        symbol={symbol}
        digits={digits}
        info={info}
        defaultVolume={clickVolume}
        busy={trading.busy}
        onPlace={trading.placePending}
        onClose={() => setPending(null)}
      />
      <Modal
        open={confirmOneClick}
        title="Turn on one-click trading?"
        onClose={() => setConfirmOneClick(false)}
      >
        <p className="mb-3 text-sm">
          With one-click trading on, the <strong>Sell</strong> and <strong>Buy</strong> buttons on this chart
          (and Alt+S / Alt+B) open a position <strong>immediately</strong>, with no confirmation. It stays
          visibly on until you turn it off.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-line px-3 py-1 text-sm hover:border-accent"
            onClick={() => setConfirmOneClick(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg"
            onClick={() => {
              prefsStore.set('oneClickAck', true);
              setConfirmOneClick(false);
              change({ oneClick: true });
            }}
          >
            Turn on
          </button>
        </div>
      </Modal>
      <ConfirmDialog
        open={confirmClearDrawings}
        title={`Delete all drawings on ${symbol}?`}
        confirmLabel="Delete all"
        onCancel={() => setConfirmClearDrawings(false)}
        onConfirm={() => {
          saveDrawings([]);
          setSelected(null);
          setConfirmClearDrawings(false);
        }}
      >
        <p>
          This removes all {drawings.length} drawing{drawings.length === 1 ? '' : 's'} on {symbol} from every
          chart and device. Undo (Ctrl+Z) on this chart brings them back until it is closed.
        </p>
      </ConfirmDialog>
    </section>
  );
}

function TemplateForm({
  symbol,
  templates,
  hasDrawings,
  onSave,
  onClose,
}: {
  symbol: string;
  templates: Record<string, unknown>;
  hasDrawings: boolean;
  onSave: (name: string, includeDrawings: boolean, includeTimeframe: boolean) => boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [includeDrawings, setIncludeDrawings] = useState(false);
  const [includeTimeframe, setIncludeTimeframe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const exists = cleanName(name) in templates;
  return (
    <form
      className="flex flex-col gap-3"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const problem = nameProblem(name, templates as never);
        if (problem) return setError(problem);
        onSave(cleanName(name), includeDrawings && hasDrawings, includeTimeframe);
      }}
    >
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wide text-muted" htmlFor="template-name">
          Template name
        </label>
        <input
          id="template-name"
          autoFocus
          maxLength={40}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          className="w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent"
        />
        {exists && (
          <p className="mt-1 text-xs text-warn">A template with this name exists; saving replaces it.</p>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={includeTimeframe}
          onChange={(e) => setIncludeTimeframe(e.target.checked)}
          className="accent-[var(--accent)]"
        />
        Include the timeframe
      </label>
      <label className={`flex items-center gap-2 text-sm ${hasDrawings ? '' : 'opacity-50'}`}>
        <input
          type="checkbox"
          disabled={!hasDrawings}
          checked={includeDrawings && hasDrawings}
          onChange={(e) => setIncludeDrawings(e.target.checked)}
          className="accent-[var(--accent)]"
        />
        Include the drawings on {symbol}
      </label>
      <p role="alert" className="min-h-[1.25rem] text-sm text-down">
        {error}
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="rounded border border-line px-3 py-1 text-sm hover:border-accent"
          onClick={onClose}
        >
          Cancel
        </button>
        <button type="submit" className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg">
          Save template
        </button>
      </div>
    </form>
  );
}

/** A toolbar icon button: the label is its accessible name and tooltip, so it is never an unlabelled glyph. */
function IconButton({ action }: { action: ToolbarAction }): ReactNode {
  const ref = useRef<HTMLButtonElement>(null);
  const tip = action.shortcut ? `${action.label} (${action.shortcut})` : action.label;
  return (
    <button
      ref={ref}
      type="button"
      aria-label={action.label}
      title={tip}
      aria-pressed={action.menu ? undefined : action.pressed}
      aria-haspopup={action.menu ? 'menu' : undefined}
      disabled={action.disabled}
      onClick={() => action.onSelect(ref.current!.getBoundingClientRect())}
      className={`rounded-md border p-[5px] disabled:opacity-40 ${
        action.pressed ? 'border-accent bg-accent text-accent-fg' : 'border-transparent hover:border-accent'
      }`}
    >
      <Icon name={action.icon} />
    </button>
  );
}
